import { get, head, uniq } from 'lodash';
import { normalize, schema } from 'normalizr';
import { v4 as uuid4 } from 'uuid';
import { formatCurrency } from 'lib/intl';
import { formatDateMedium, parseStringDate } from 'lib/date';
import { formatSortCode } from 'lib/misc';
import {
  BankAccount,
  BankAccountDTO,
  BankAccountEntityResponse,
  BankAccountManageResponse,
  BankAccountSummaryDTO,
  BankAccountSummaryResponse,
  BankTransaction,
  BankTransactionEntityResponse,
  BankTransactionListResponse,
  Note,
  NoteEntityResponse,
  NoteListResponse,
  SystemTransaction,
  SystemTransactionEntityResponse,
  SystemTransactionListResponse,
  SystemTransactionPaymentLine,
} from 'types/dto';

const normalizationKey = 'value' as const;

// Map of entity identifiers and actual entities
type EntityMap<TEntity> = {
  [key: string]: TEntity;
};

// Result of the internal normalization call
type NormalizationMap<TEntity> = {
  [key in typeof normalizationKey]: EntityMap<TEntity>;
};

// Output of a normalization function
type NormalizationResult<TEntity> = [EntityMap<TEntity>, Array<string>];

/**
 * Entity and response object schemas
 */
const bankAccountEntitySchema = new schema.Entity<BankAccount>(normalizationKey, undefined, {
  processStrategy: (dto: BankAccountDTO) => ({
    ...dto,
    openingBalanceFormatted: formatCurrency(dto.openingBalance),
    sortCodeFormatted: formatSortCode(dto.sortCode),
    openingBalanceDate: dto.openingBalanceDate
      ? parseStringDate(dto.openingBalanceDate)
      : undefined,
  }),
});

const bankAccountSummarySchema = new schema.Entity<BankAccount>(
  normalizationKey,
  {
    bankAccount: bankAccountEntitySchema,
  },
  {
    idAttribute: (dto: BankAccountSummaryDTO) => dto.bankAccount.id,
    processStrategy: (dto: BankAccountSummaryDTO) => {
  const lastReconciledDate = dto.lastReconciledDate
    ? parseStringDate(dto.lastReconciledDate)
    : undefined;
  const openingBalanceDate = dto.bankAccount.openingBalanceDate
    ? parseStringDate(dto.bankAccount.openingBalanceDate)
    : undefined;
  return {
    ...dto.bankAccount,
    lastKnownBalance: dto.lastKnownBalance,
    lastKnownBalanceFormatted: formatCurrency(dto.lastKnownBalance),
    totalBalance: dto.totalBalance,
    totalBalanceFormatted: formatCurrency(dto.totalBalance),
    lastReconciledDate,
    lastReconciledDateFormatted: lastReconciledDate
      ? formatDateMedium(lastReconciledDate)
      : undefined,
    openingBalanceFormatted: formatCurrency(dto.bankAccount.openingBalance),
    sortCodeFormatted: formatSortCode(dto.bankAccount.sortCode),
    openingBalanceDate,
    };
   },
  },
);

const bankAccountEntityResponseSchema = new schema.Object<BankAccountEntityResponse>({
  bankAccount: bankAccountEntitySchema,
});

const bankAccountListResponseSchema = new schema.Object<BankAccountManageResponse>({
  bankAccounts: new schema.Array(bankAccountEntitySchema),
});

const bankAccountSummaryResponseSchema = new schema.Object<BankAccountSummaryResponse>({
  bankSummary: new schema.Array(bankAccountSummarySchema),
});

const bankTransactionEntitySchema = new schema.Entity<BankTransaction>(
  normalizationKey,
  undefined,
  {
    processStrategy: (dto: BankTransactionEntityResponse) => ({
      ...dto,
      amountFormatted: formatCurrency(dto.amount),
      amountString: dto.amount.toFixed(2),
      transactionDate: parseStringDate(dto.transactionDate),
      transactionDateFormatted: formatDateMedium(dto.transactionDate),
    }),
  },
);

const noteEntitySchema = new schema.Entity<Note>(normalizationKey, undefined, {
  processStrategy: (dto: NoteEntityResponse) => ({
    ...dto,
    createdAt: parseStringDate(dto.createdAt),
  }),
});

const bankTransactionListResponseSchema = new schema.Object<BankTransactionListResponse>({
  bankTransactions: new schema.Array(bankTransactionEntitySchema),
});

const systemTransactionEntitySchema = new schema.Entity<SystemTransaction>(
  normalizationKey,
  undefined,
  {
    processStrategy: (dto: SystemTransactionEntityResponse) => {
      const { paymentLines, ...rest } = dto;

      // Update payment line items to include an identifier needed in tables
      const lines: Array<SystemTransactionPaymentLine> = (paymentLines ?? []).map(paymentLine => ({
        id: uuid4(),
        ...paymentLine,
        amountFormatted: formatCurrency(paymentLine.amount),
      }));

      // Retrieve the first payment line
      const firstLine = head(lines);

      // List of keys to transfer from the first payment line to the root entity
      const transferKeys: Array<IntersectedKeys<SystemTransactionPaymentLine, SystemTransaction>> =
        ['narrative', 'ref1'];

      // Build a list of keys and values to apply to the root entity from the first payment line
      const transferMap = transferKeys.reduce<Partial<SystemTransaction>>(
        (acc, prop) => ({
          ...acc,
          [prop]: get(firstLine, prop, null),
        }),
        {},
      );

      return {
        ...rest,
        ...transferMap,
        amountFormatted: formatCurrency(dto.amount),
        amountString: dto.amount.toFixed(2),
        paymentLines: lines,
        transactionDate: parseStringDate(dto.transactionDate),
        transactionDateFormatted: formatDateMedium(dto.transactionDate),
      } as SystemTransaction;
    },
  },
);

const systemTransactionListResponseSchema = new schema.Object<SystemTransactionListResponse>({
  systemTransactions: new schema.Array(systemTransactionEntitySchema),
});

const noteEntityListResponseSchema = new schema.Array<NoteEntityResponse>(noteEntitySchema);

/**
 * Create a normalization result from the specified entity information
 *
 * @param {NormalizationMap} map Entity map
 * @param {Array} ids Entity identifier list
 */
const createNormalizationResult = <TEntity>(
  map?: NormalizationMap<TEntity>,
  ids?: Array<string>,
): NormalizationResult<TEntity> => [map?.value ?? {}, uniq(ids)];

/**
 * Normalize bank account response
 *
 * @param {BankAccountEntityResponse} response Response object
 */
export const normalizeBankAccountEntityResponse = (response: BankAccountEntityResponse) => {
  const { entities, result } = normalize<
    typeof response,
    NormalizationMap<BankAccount>,
    { bankAccount: string }
  >(response, bankAccountEntityResponseSchema);

  return createNormalizationResult(entities, [result.bankAccount]);
};

/**
 * Normalize bank account list response
 *
 * @param {BankAccountManageResponse} response Response object
 */
export const normalizeBankAccountManageResponse = (response: BankAccountManageResponse) => {
  const { entities, result } = normalize<
    typeof response,
    NormalizationMap<BankAccount>,
    { bankAccounts: Array<string> }
  >(response, bankAccountListResponseSchema);

  return createNormalizationResult(entities, result.bankAccounts);
};

/**
 * Normalize bank account summary response
 *
 * @param {BankAccountSummaryResponse} response Response object
 */
export const normalizeBankAccountSummaryResponse = (response: BankAccountSummaryResponse) => {
  const { entities, result } = normalize<
    typeof response,
    NormalizationMap<BankAccount>,
    { bankSummary: Array<string> }
  >(response, bankAccountSummaryResponseSchema);

  return createNormalizationResult(entities, result.bankSummary);
};

/**
 * Normalize bank transaction response
 *
 * @param {BankTransactionListResponse} response Response object
 */
export const normalizeBankTransactionListResponse = (response: BankTransactionListResponse) => {
  const { entities, result } = normalize<
    typeof response,
    NormalizationMap<BankTransaction>,
    { bankTransactions: Array<string> }
  >(response, bankTransactionListResponseSchema);

  return createNormalizationResult(entities, result.bankTransactions);
};

/**
 * Normalize transaction note response
 *
 * @param {NoteListResponse} response Response object
 */
export const normalizeNoteEntityResponse = (response: NoteEntityResponse) => {
  const { entities, result } = normalize<typeof response, NormalizationMap<Note>, string>(
    { ...response, id: uuid4() },
    noteEntitySchema,
  );

  return entities.value[result];
};

/**
 * Normalize transaction note response
 *
 * @param {NoteListResponse} response Response object
 */
export const normalizeNoteListResponse = (response: NoteListResponse) => {
  const { entities } = normalize<typeof response, NormalizationMap<Note>>(
    response.map(note => ({ ...note, id: uuid4() })),
    noteEntityListResponseSchema,
  );

  return Object.values(entities.value ?? {});
};

/**
 * Normalize system transaction response
 *
 * @param {SystemTransactionListResponse} response Response object
 */
export const normalizeSystemTransactionListResponse = (response: SystemTransactionListResponse) => {
  const { entities, result } = normalize<
    typeof response,
    NormalizationMap<SystemTransaction>,
    { systemTransactions: Array<string> }
  >(response, systemTransactionListResponseSchema);

  return createNormalizationResult(entities, result.systemTransactions);
};
